/**
 * useSponsorRequest Hook
 *
 * Custom hook for creating sponsor advisory requests
 * Handles form submission and request creation
 */

import { useState, useCallback } from 'react';

import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { advisoryService } from 'services/advisoryService';
import { CreateLeadRequestDTO, LeadRequest } from 'types/leadRequestsTypes';

interface UseSponsorRequestReturn {
  createRequest: (formResponses: Record<string, any>) => Promise<LeadRequest | null>;
  loading: boolean;
  error: string | null;
  requestId: string | null;
}

export const useSponsorRequest = (): UseSponsorRequestReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const { toastError, toastSuccess } = useToast();
  const { user } = useUser();

  const createRequest = useCallback(
    async (formResponses: Record<string, any>): Promise<LeadRequest | null> => {
      if (!user?.id) {
        const errorMsg = 'User not authenticated';
        setError(errorMsg);
        toastError(errorMsg);
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const requestData: CreateLeadRequestDTO = {
          requester_user_id: user.id,
          form_responses: formResponses,
        };

        const response = await advisoryService.createLeadRequest(requestData);

        setRequestId(response.id);
        toastSuccess('Solicitação de lead criada com sucesso!');

        return response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || 'Falha ao criar solicitação de lead';
        setError(errorMessage);
        toastError(errorMessage);
        console.error('Erro ao criar solicitação de lead:', err);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user, toastError, toastSuccess]
  );

  return {
    createRequest,
    loading,
    error,
    requestId,
  };
};
